import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { onAuthStateChanged, signInWithCustomToken } from "firebase/auth";
import { appAbility, defineRulesFor } from "../services/ability.service";
import { registerAuthSessionResetHandler, endAuthSession, resetAuthSession, } from "../services/auth-session.service";
import { encrypted } from "@/plugins/crypto";
import api from "@/infrastructure/api/axios";
import { auth } from "@/infrastructure/firebase";
import { resolveAppLocale } from "@/i18n/locale";
// =============================================================================
// STORE
// =============================================================================
export const useAuthStore = defineStore("auth", () => {
    // ── STATE ──────────────────────────────────────────────────────────────────
    const user = ref(null);
    const currentOrgId = ref(null);
    const roleKeys = ref([]);
    const isSuperAdmin = ref(false);
    const contexts = ref([]);
    const memberships = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const sessionState = ref("loading");
    let authStateListenerRegistered = false;
    // ── GETTERS ────────────────────────────────────────────────────────────────
    const isAuthenticated = computed(() => !!user.value);
    const primaryRole = computed(() => roleKeys.value[0] ?? null);
    const activeMemberships = computed(() => memberships.value.filter((membership) => membership.org_status !== "inactive" && membership.is_active !== false));
    const currentMembership = computed(() => memberships.value.find((membership) => String(membership.org_id) === String(currentOrgId.value)) ?? null);
    const currentOrgName = computed(() => currentMembership.value?.org_name ?? "");
    const currentOrgType = computed(() => currentMembership.value?.org_type ?? null);
    const hasMultipleOrganizations = computed(() => activeMemberships.value.length > 1);
    const shouldSelectOrganization = computed(() => isSuperAdmin.value || hasMultipleOrganizations.value);
    const isReadOnlyArchive = computed(() => isSuperAdmin.value &&
        !!currentOrgId.value &&
        (currentMembership.value?.org_status === "inactive" ||
            currentMembership.value?.org_status === "deleted" ||
            currentMembership.value?.is_active === false));
    registerAuthSessionResetHandler(resetStateOnly);
    // ── ACTIONS ────────────────────────────────────────────────────────────────
    /**
     * Fetches the current backend session to load selectable auth contexts,
     * then restores or selects the active context and rebuilds the CASL ability.
     * Called after Firebase sign-in and during startup hydration.
     */
    async function fetchUserProfile() {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await api.get("/api/v2/sessions/current");
            const introspection = normalizeIntrospectionResponse(response.data.data);
            if (introspection.user) {
                user.value = normalizeUser(introspection.user);
                localStorage.setItem("user", encrypted(user.value));
            }
            contexts.value = (introspection.contexts ?? []).map(normalizeContext);
            memberships.value = contexts.value
                .filter((context) => context.mode === "tenant")
                .map(contextToMembership);
            isSuperAdmin.value = hasGlobalPlatformOwnerContext();
            const storedOrgId = localStorage.getItem("org_id");
            const storedRoleKey = localStorage.getItem("role_key");
            const storedMembership = storedOrgId
                ? memberships.value.find((membership) => String(membership.org_id) === storedOrgId &&
                    (isSuperAdmin.value ||
                        (membership.org_status !== "inactive" &&
                            membership.org_status !== "deleted" &&
                            membership.is_active !== false)))
                : null;
            if (storedMembership) {
                selectOrgContext(storedMembership);
            }
            else if (isSuperAdmin.value && storedRoleKey === "platform_owner") {
                selectGlobalContext();
            }
            else if (!isSuperAdmin.value && activeMemberships.value.length === 1) {
                selectOrgContext(activeMemberships.value[0]);
            }
            else {
                clearOrgContext();
            }
            localStorage.setItem("auth_contexts", encrypted(contexts.value));
            localStorage.setItem("memberships", encrypted(memberships.value));
            _rebuildAbility();
            sessionState.value = "authenticated";
        }
        catch (e) {
            error.value = "Error al cargar el perfil de usuario";
            throw e;
        }
        finally {
            isLoading.value = false;
        }
    }
    /**
     * Switches the active org and updates the role/ability accordingly.
     * Looks up the role_key for the given org from the stored memberships.
     */
    function switchOrg(orgId) {
        const membership = activeMemberships.value.find((m) => String(m.org_id) === String(orgId));
        if (!membership)
            return;
        selectOrgContext(membership);
    }
    function selectOrgContext(membership) {
        currentOrgId.value = String(membership.org_id);
        roleKeys.value = [membership.role_key];
        const existingIndex = memberships.value.findIndex((item) => String(item.org_id) === String(membership.org_id));
        if (existingIndex >= 0) {
            memberships.value.splice(existingIndex, 1, normalizeMembership(membership));
        }
        else if (isSuperAdmin.value) {
            memberships.value.push(normalizeMembership(membership));
        }
        localStorage.setItem("org_id", String(membership.org_id));
        localStorage.setItem("role_key", membership.role_key);
        localStorage.setItem("memberships", encrypted(memberships.value));
        _rebuildAbility();
    }
    function selectGlobalContext() {
        if (!hasGlobalPlatformOwnerContext())
            return;
        currentOrgId.value = null;
        roleKeys.value = ["platform_owner"];
        localStorage.removeItem("org_id");
        localStorage.setItem("role_key", "platform_owner");
        _rebuildAbility();
    }
    function clearOrgContext() {
        currentOrgId.value = null;
        roleKeys.value = [];
        localStorage.removeItem("org_id");
        localStorage.removeItem("role_key");
        _rebuildAbility();
    }
    async function login(email, password) {
        isLoading.value = true;
        error.value = null;
        try {
            const response = await api.post("/api/v2/sessions", { authenticationType: "password", email, password });
            const { credentialType, credential } = response.data.data;
            if (credentialType !== "firebase_custom_token" || !credential) {
                throw new Error("No Firebase custom token received");
            }
            await signInWithCustomToken(auth, credential);
            await fetchUserProfile();
        }
        catch (e) {
            if (isUnauthorizedError(e)) {
                if (auth.currentUser)
                    await endAuthSession();
                else
                    resetAuthSession();
                sessionState.value = "unauthenticated";
            }
            else {
                resetAuthSession();
                sessionState.value = auth.currentUser ? "error" : "unauthenticated";
            }
            error.value = "Credenciales inválidas o error de servidor";
            throw e;
        }
        finally {
            isLoading.value = false;
        }
    }
    async function logout() {
        await endAuthSession();
    }
    function resetStateOnly() {
        user.value = null;
        currentOrgId.value = null;
        roleKeys.value = [];
        isSuperAdmin.value = false;
        contexts.value = [];
        memberships.value = [];
        isLoading.value = false;
        error.value = null;
        sessionState.value = "unauthenticated";
    }
    async function initialize() {
        if (sessionState.value === "authenticated" ||
            sessionState.value === "unauthenticated") {
            return;
        }
        sessionState.value = "loading";
        await auth.authStateReady();
        if (!authStateListenerRegistered) {
            onAuthStateChanged(auth, (firebaseUser) => {
                if (!firebaseUser)
                    resetAuthSession();
            });
            authStateListenerRegistered = true;
        }
        if (!auth.currentUser) {
            resetAuthSession();
            return;
        }
        try {
            await fetchUserProfile();
        }
        catch {
            resetAuthSession();
            if (!auth.currentUser)
                return;
            sessionState.value = "error";
            error.value = "Error al cargar el perfil de usuario";
        }
    }
    function $reset() {
        logout();
    }
    // ── PRIVATE ────────────────────────────────────────────────────────────────
    function _rebuildAbility() {
        const ability = defineRulesFor(roleKeys.value, false);
        appAbility.update(ability.rules);
    }
    function normalizeUser(introspectedUser) {
        return {
            user_id: String(introspectedUser?.user_id ??
                introspectedUser?.id ??
                user.value?.user_id ??
                ""),
            name: String(introspectedUser?.name ?? user.value?.name ?? ""),
            email: String(introspectedUser?.email ?? user.value?.email ?? ""),
            preferred_language: normalizePreferredLanguage(introspectedUser?.preferred_language ?? user.value?.preferred_language),
            ...(introspectedUser?.dni ? { dni: String(introspectedUser.dni) } : {}),
        };
    }
    function normalizePreferredLanguage(language) {
        return resolveAppLocale(language);
    }
    function isUnauthorizedError(error) {
        return (error?.response?.status === 401);
    }
    function normalizeContext(context) {
        const orgType = context.org_type ??
            context.organization?.org_type ??
            context
                .organization?.type;
        const mode = context.mode ??
            context.context_type ??
            (context.org_id ? "tenant" : "global");
        const roleKey = normalizeContextRole(context.role_key, mode, orgType ?? undefined);
        return {
            ...context,
            mode,
            context_type: undefined,
            role_key: roleKey,
            role_label: context.role_label ?? getRoleLabel(roleKey),
            org_id: context.org_id == null ? null : Number(context.org_id),
            org_name: context.org_name ??
                context
                    .organization?.display_name,
            ...(orgType ? { org_type: orgType } : {}),
            org_status: context.org_status ??
                (context.is_active === false ? "inactive" : "active"),
            is_active: context.is_active ??
                (context.org_status !== "inactive" && context.org_status !== "deleted"),
        };
    }
    function normalizeIntrospectionResponse(response) {
        return {
            user: response.user,
            contexts: response.contexts.flatMap((context) => {
                const normalizedContext = normalizeIntrospectionContext(context);
                return normalizedContext ? [normalizedContext] : [];
            }),
        };
    }
    function normalizeIntrospectionContext(context) {
        const orgId = context.orgId == null ? null : Number(context.orgId);
        if (!context.roleKey || (orgId !== null && !Number.isFinite(orgId))) {
            return null;
        }
        return {
            mode: orgId === null ? "global" : "tenant",
            org_id: orgId,
            org_name: context.orgName,
            org_type: context.orgType,
            role_key: context.roleKey,
            role_label: context.roleLabel,
            org_status: context.orgStatus,
            is_active: context.isActive,
            derived_from: context.derivedFrom,
        };
    }
    function normalizeContextRole(roleKey, contextType, orgType) {
        if (contextType === "global")
            return "platform_owner";
        if (roleKey === "platform_owner" ||
            roleKey === "super_admin" ||
            roleKey === "admin") {
            return orgType === "concierge" ? "concierge_owner" : "provider_owner";
        }
        return roleKey;
    }
    function contextToMembership(context) {
        return normalizeMembership({
            org_id: Number(context.org_id),
            role_key: context.role_key,
            role_label: context.role_label,
            org_name: context.org_name,
            ...(context.org_type ? { org_type: context.org_type } : {}),
            org_status: context.org_status,
            is_active: context.is_active,
        });
    }
    function hasGlobalPlatformOwnerContext() {
        return contexts.value.some((context) => context.mode === "global" &&
            context.role_key === "platform_owner" &&
            context.is_active !== false);
    }
    function restoreStoredContextSelection() {
        const storedOrgId = localStorage.getItem("org_id");
        const storedRoleKey = localStorage.getItem("role_key");
        if (storedOrgId) {
            const archivedMembership = isSuperAdmin.value
                ? memberships.value.find((membership) => String(membership.org_id) === storedOrgId &&
                    (!storedRoleKey || membership.role_key === storedRoleKey))
                : null;
            if (archivedMembership) {
                selectOrgContext(archivedMembership);
                return;
            }
            const storedMembership = activeMemberships.value.find((membership) => String(membership.org_id) === storedOrgId &&
                (!storedRoleKey || membership.role_key === storedRoleKey));
            if (storedMembership) {
                selectOrgContext(storedMembership);
                return;
            }
        }
        if (!storedOrgId &&
            storedRoleKey === "platform_owner" &&
            hasGlobalPlatformOwnerContext()) {
            selectGlobalContext();
            return;
        }
        clearOrgContext();
    }
    function normalizeMembership(membership) {
        const orgName = membership.org_name ??
            membership.organization?.display_name ??
            `Organización ${membership.org_id}`;
        const orgType = membership.org_type ??
            membership.organization?.org_type ??
            membership.organization?.type;
        return {
            ...membership,
            org_id: Number(membership.org_id),
            role_label: membership.role_label ?? getRoleLabel(membership.role_key),
            org_name: orgName,
            ...(orgType ? { org_type: orgType } : {}),
            org_status: membership.org_status ??
                (membership.is_active === false ? "inactive" : "active"),
            is_active: membership.is_active ??
                (membership.org_status !== "inactive" &&
                    membership.org_status !== "deleted"),
        };
    }
    function getRoleLabel(roleKey) {
        const labels = {
            provider_owner: "Propietario proveedor",
            provider_admin: "Administrador proveedor",
            provider_staff: "Equipo proveedor",
            concierge_owner: "Propietario concierge",
            concierge_admin: "Administrador concierge",
            concierge_staff: "Equipo concierge",
            platform_owner: "Propietario plataforma",
            admin: "Administrador",
            org_admin: "Administrador",
            provider: "Proveedor",
            proveedor: "Proveedor",
            concierge: "Concierge",
        };
        return labels[roleKey] ?? roleKey;
    }
    // ── RETURN ─────────────────────────────────────────────────────────────────
    return {
        user,
        currentOrgId,
        roleKeys,
        isSuperAdmin,
        contexts,
        memberships,
        isLoading,
        error,
        sessionState,
        isAuthenticated,
        primaryRole,
        activeMemberships,
        currentMembership,
        currentOrgName,
        currentOrgType,
        hasMultipleOrganizations,
        shouldSelectOrganization,
        isReadOnlyArchive,
        fetchUserProfile,
        initialize,
        switchOrg,
        selectOrgContext,
        selectGlobalContext,
        clearOrgContext,
        login,
        logout,
        $reset,
    };
});
